if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();
        this.quantityInput = this.querySelector('.quantity__input');
        this.hasAddons = () => document.querySelector('.custom-options-container') !== null;
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();

        queueMicrotask(() => { try { window?.Shopify?.PaymentButton?.init(); } catch (e) {} });

        try {
          const v = this.getSelectedVariant(this);
          if (v) this.productForm?.toggleSubmitButton(false);
        } catch (e) {}

        this.mountAddons();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      mountAddons() {
        const sid = this.sectionId;
        const mount =
          this.querySelector(`#addons-mount-${sid}`) ||
          this.querySelector('.addons-mount');
        if (!mount) return;

        let src =
          document.getElementById(`addons-source-${sid}`) ||
          document.querySelector('[id^="addons-source-"]');
        if (!src) return;

        mount.innerHTML = src.innerHTML;
        mount.style.display = '';
        this.dispatchEvent(new CustomEvent('addons:mounted', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;
        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;
        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) => {
          html.querySelectorAll('.scroll-trigger').forEach((el) => el.classList.add('scroll-trigger--cancel'));
        });

        this.postProcessHtmlCallbacks.push(() => {
          this.mountAddons();
        });

        this.postProcessHtmlCallbacks.push(() => {
          try { window?.Shopify?.PaymentButton?.init(); } catch (_) {}
          try { window?.ProductModel?.loadShopifyXR?.(); } catch (_) {}
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        // Only respond if the change happened inside THIS product-info
        if (!this.contains(event?.target)) return;

        // If an add-on option specifically triggered this, skip the product info refetch
        if (target?.closest('.custom-options-container')) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;
            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.debug('Fetch aborted');
              try {
                const v = this.getSelectedVariant(this);
                if (v) this.productForm?.toggleSubmitButton(false);
              } catch (_) {}
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode?.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];
        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);
        if (optionValues.length) params.push(`option_values=${optionValues.join(',')}`);
        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      findSectionId() {
        const priceElement = document.querySelector('[id*="price-"]:not([id*="undefined"])');
        if (priceElement) {
          const match = priceElement.id.match(/price-(.+)$/);
          if (match) return match[1];
        }
        const skuElement = document.querySelector('[id*="Sku-"]:not([id*="undefined"])');
        if (skuElement) {
          const match = skuElement.id.match(/Sku-(.+)$/);
          if (match) return match[1];
        }
        const productForm = document.querySelector('[id*="product-form-"]');
        if (productForm) {
          const match = productForm.id.match(/product-form-(.+)$/);
          if (match) return match[1];
        }
        return null;
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          try {
            let currentSectionId = this.sectionId;
            if (!currentSectionId || currentSectionId === 'undefined') {
              currentSectionId = this.findSectionId();
            }
            if (!currentSectionId) {
              console.error('Could not determine section ID');
              return;
            }

            const variant = this.getSelectedVariant(html);

            try { this.pickupAvailability?.update(variant); } catch (e) {}
            try { this.updateOptionValues(html); } catch (e) {}

            this.updateURL(productUrl, variant?.id);
            this.updateVariantInputs(variant?.id);

            if (!variant) {
              this.setUnavailable();
              return;
            }

            try { this.updateMedia(html, variant?.featured_media?.id); } catch (e) {}

            const updateElement = (elementId, shouldHide = () => false) => {
              try {
                const sourceId = `${elementId}-${currentSectionId}`;
                const source = html.getElementById(sourceId);
                const destination = document.getElementById(sourceId);
                if (source && destination) {
                  destination.innerHTML = source.innerHTML;
                  destination.classList.toggle('hidden', shouldHide(source));
                  return true;
                }
                return false;
              } catch (e) {
                console.error(`Error updating ${elementId}:`, e);
                return false;
              }
            };

            updateElement('price');
            updateElement('Sku', (source) => source.classList.contains('hidden'));
            updateElement('Inventory', (source) => source.innerText.trim() === '');
            updateElement('Volume');
            updateElement('Price-Per-Item', (source) => source.classList.contains('hidden'));

            try {
              this.updateQuantityRules(currentSectionId, html);
              document.querySelector(`#Quantity-Rules-${currentSectionId}`)?.classList.remove('hidden');
              document.querySelector(`#Volume-Note-${currentSectionId}`)?.classList.remove('hidden');
            } catch (e) {}

            try {
              this.productForm?.toggleSubmitButton(
                html.getElementById(`ProductSubmitButton-${currentSectionId}`)?.hasAttribute('disabled') ?? true,
                window.variantStrings.soldOut
              );
            } catch (e) {}

            try {
              publish(PUB_SUB_EVENTS.variantChange, {
                data: { sectionId: currentSectionId, html, variant },
              });
            } catch (e) {}

          } catch (error) {
            console.error('handleUpdateProductInfo failed:', error);
          }
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );
        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);
        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.sectionId}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        try {
          const mediaGallerySource = this.querySelector('media-gallery ul');
          const mediaGalleryDestination = html.querySelector('media-gallery ul');

          const refreshSourceData = () => {
            try {
              if (this.hasAttribute('data-zoom-on-hover') && typeof enableZoomOnHover === 'function') {
                enableZoomOnHover(2);
              }
            } catch (e) {}

            if (!mediaGallerySource) return [[], new Set(), new Map()];

            const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
            const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
            const sourceMap = new Map(
              mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
            );
            return [mediaGallerySourceItems, sourceSet, sourceMap];
          };

          if (mediaGallerySource && mediaGalleryDestination) {
            let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            const mediaGalleryDestinationItems = Array.from(
              mediaGalleryDestination.querySelectorAll('li[data-media-id]')
            );
            const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
            let shouldRefresh = false;

            for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
              if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
                mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
                shouldRefresh = true;
              }
            }

            for (let i = 0; i < mediaGallerySourceItems.length; i++) {
              if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
                mediaGallerySourceItems[i].remove();
                shouldRefresh = true;
              }
            }

            if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

            mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
              const sourceData = sourceMap.get(destinationItem.dataset.mediaId);
              if (sourceData && sourceData.index !== destinationIndex) {
                const insertBeforeElement = mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`);
                if (insertBeforeElement) mediaGallerySource.insertBefore(sourceData.item, insertBeforeElement);
                [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
              }
            });
          }

          try {
            this.querySelector('media-gallery')?.setActiveMedia?.(
              `${this.dataset.section}-${variantFeaturedMediaId}`,
              true
            );
          } catch (e) {}

          try {
            const modalContent = this.productModal?.querySelector('.product-media-modal__content');
            const newModalContent = html.querySelector('product-modal .product-media-modal__content');
            if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
          } catch (e) {}

        } catch (error) {
          console.error('Media update failed:', error);
        }
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;
        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;
        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner')?.classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner')?.classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated?.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() { return this.querySelector('product-form'); }
      get productModal() { return document.querySelector(`#ProductModal-${this.dataset.section}`); }
      get pickupAvailability() { return this.querySelector('pickup-availability'); }
      get variantSelectors() { return this.querySelector('variant-selects'); }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId), 'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId), 'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        let id =
          this.dataset.originalSection ||
          this.dataset.section ||
          this.getAttribute('data-section') ||
          this.getAttribute('data-original-section');

        if (!id || id === 'undefined') {
          try { id = this.findSectionId(); } catch (_) {}
        }

        return id;
      }
    }
  );
}